import { Exercise as ComponentExercise, ExerciseType, FillInBlanksData } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { FormInput } from "lucide-react";
import { ExerciseType as InternalExerciseType } from "./types";

/**
 * Editor para exercícios de escrever a tradução (versão unificada)
 * Focado em exercícios onde o aluno precisa traduzir um texto
 */
export class WriteTheTranslationInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício de escrever a tradução
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || !WriteTheTranslationInfo.SupportsExerciseType(exercise.type as unknown as string)) {
      return {
        title: 'Escrever a Tradução',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      // Tenta extrair os dados do exercício, lidando com diferentes formatos
      let originalText = 'Sem texto original';
      let translatedText = 'Sem tradução';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as FillInBlanksData;
        originalText = data.text || 'Sem texto original';
        translatedText = data.blanks && data.blanks.length > 0 ? data.blanks[0] : 'Sem tradução';
      } 
      // Se os dados estiverem no formato plano
      else if ('originalText' in exercise) {
        originalText = (exercise as any).originalText || 'Sem texto original';
        translatedText = (exercise as any).translatedText || 'Sem tradução';
      }
      // Formato alternativo com text e blanks
      else if ('text' in exercise) {
        originalText = (exercise as any).text || 'Sem texto original';
        translatedText = (exercise as any).blanks && (exercise as any).blanks.length > 0 
          ? (exercise as any).blanks[0] 
          : 'Sem tradução';
      }
      
      return {
        title: "Escrever a Tradução",
        subtitle: originalText.length > 60 ? `${originalText.substring(0, 60)}...` : originalText,
        description: `Tradução: ${translatedText.length > 30 ? `${translatedText.substring(0, 30)}...` : translatedText}`,
      };
    } catch (error) {
      console.error("Erro ao processar dados do exercício de escrever a tradução:", error);
      return {
        title: 'Escrever a Tradução',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Retorna o título do tipo de exercício
   */
  static getTitle(): string {
    return "Escrever a Tradução";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      // Tenta extrair a tradução do exercício
      let translatedText = 'Sem tradução';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as FillInBlanksData;
        translatedText = data.blanks && data.blanks.length > 0 ? data.blanks[0] : 'Sem tradução';
      } 
      // Se os dados estiverem no formato plano
      else if ('translatedText' in exercise) {
        translatedText = (exercise as any).translatedText || 'Sem tradução';
      }
      // Formato alternativo com blanks
      else if ('blanks' in exercise) {
        translatedText = (exercise as any).blanks && (exercise as any).blanks.length > 0 
          ? (exercise as any).blanks[0] 
          : 'Sem tradução';
      }
      
      return translatedText !== 'Sem tradução' 
        ? `Tradução: ${translatedText.length > 50 ? `${translatedText.substring(0, 50)}...` : translatedText}`
        : "Sem tradução definida";
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para o tipo de exercício
   */
  static getIcon() {
    return FormInput;
  }
  
  /**
   * Verifica se o exercício é do tipo Escrever a Tradução
   */
  static SupportsExerciseType(type: string): boolean {
    return type === InternalExerciseType.WriteTheTranslation;
  }
}